import React, { useEffect, useRef, useState } from "react";
import { Panel } from "@/pages/admin/shared";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "@/pages/admin/api";

const CATEGORIES = ["general", "logo", "hero", "about", "invoice", "icon", "background", "favicon"];

export default function MediaManager() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("general");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/media").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("File too large (max 3MB)"); return; }
    const form = new FormData();
    form.append("file", f);
    setUploading(true);
    try {
      await api.post(`/media?category=${encodeURIComponent(category)}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    await api.delete(`/media/${id}`);
    toast.success("Deleted");
    load();
  };

  const copy = async (url) => { await navigator.clipboard.writeText(url); toast.success("URL copied"); };

  return (
    <Panel
      title="Media Library"
      subtitle={`${items.length} files`}
      actions={
        <div className="flex items-center gap-2">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="media-file-input" />
          <Button data-testid="upload-media" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
            <Upload size={14} className="mr-1" /> {uploading ? "Uploading..." : "Upload"}
          </Button>
        </div>
      }
    >
      {items.length === 0 && <div className="text-sm text-muted-foreground py-10 text-center">No media yet. Upload logos, hero images, favicons, etc.</div>}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {items.map((m) => (
          <div key={m.id} className="group rounded-xl border border-border overflow-hidden bg-slate-50 dark:bg-slate-800 relative">
            <div className="aspect-square bg-white dark:bg-slate-900 grid place-items-center overflow-hidden">
              {m.mime?.startsWith("image/") ? <img src={m.url} alt={m.filename} className="w-full h-full object-cover" /> : <div className="text-xs text-muted-foreground">{m.mime}</div>}
            </div>
            <div className="p-2">
              <div className="text-[10px] font-medium truncate">{m.filename}</div>
              <div className="text-[10px] text-muted-foreground">{Math.round(m.size / 1024)} KB · {m.category}</div>
            </div>
            <div className="absolute top-1 right-1 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => copy(m.url)} aria-label="Copy URL" className="w-7 h-7 rounded-md bg-white/90 text-slate-700 grid place-items-center shadow-sm hover:bg-white"><Copy size={12} /></button>
              <button onClick={() => remove(m.id)} aria-label="Delete" className="w-7 h-7 rounded-md bg-white/90 text-red-500 grid place-items-center shadow-sm hover:bg-white"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}
